/**
 * SignupForm.js
 *
 * Shared signup form component used in both regular signup and gift pool acceptance flows.
 * Provides consistent UI for account creation with name, email, and password fields.
 */

import React from 'react';
import StripeSetupInfo from './StripeSetupInfo';

const inputClass =
  'appearance-none block w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition-colors';
const labelClass = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';

function SignupForm({
  name,
  email,
  password,
  confirmPassword,
  onNameChange,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  error,
  loading,
  onSubmit,
  submitButtonText = 'Sign up',
  showEmailField = true,
  emailReadOnly = false,
  nameLabel = 'Name',
  namePlaceholder = 'John Doe',
}) {
  return (
    <form className="space-y-5" onSubmit={onSubmit}>
      <div>
        <label htmlFor="name" className={labelClass}>
          {nameLabel}
        </label>
        <input
          id="name"
          name="name"
          type="text"
          required
          value={name}
          onChange={onNameChange}
          className={inputClass}
          placeholder={namePlaceholder}
        />
      </div>

      {showEmailField && (
        <div>
          <label htmlFor="email" className={labelClass}>
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            readOnly={emailReadOnly}
            value={email}
            onChange={onEmailChange}
            className={`${inputClass} ${emailReadOnly ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            placeholder="you@example.com"
          />
        </div>
      )}

      <div>
        <label htmlFor="password" className={labelClass}>
          Password
        </label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={onPasswordChange}
          className={inputClass}
          placeholder="••••••••"
        />
        <p className="mt-1.5 text-xs text-gray-400">Must be at least 6 characters</p>
      </div>

      <div>
        <label htmlFor="confirmPassword" className={labelClass}>
          Confirm Password
        </label>
        <input
          id="confirmPassword"
          name="confirmPassword"
          type="password"
          autoComplete="new-password"
          required
          value={confirmPassword}
          onChange={onConfirmPasswordChange}
          className={inputClass}
          placeholder="••••••••"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Stripe Express Info */}
      <StripeSetupInfo />

      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-indigo-500 hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
      >
        {loading ? 'Creating account...' : submitButtonText}
      </button>
    </form>
  );
}

export default SignupForm;
